import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Card, Form, Button, ListGroup, Badge, Spinner } from 'react-bootstrap';
import { BsChatDots, BsX, BsSend, BsChevronDown, BsPersonCircle } from 'react-icons/bs';
import './FloatingChatWindow.css';
import {
  createIndividualChat,
  sendMessage,
  listenToMessages,
  loadOlderMessages,
  getUserChats,
  resetUnreadCount
} from '../../../firebase/chatService';
import { authenticateWithFirebase } from '../../../firebase/firebaseAuth';

const FloatingChatWindow = forwardRef(({ currentUser, allUsers = [] }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showUserList, setShowUserList] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [userChats, setUserChats] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [allMessages, setAllMessages] = useState([]); // Combined messages from real-time and pagination
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const unsubscribeMessages = useRef(null);
  const unsubscribeChats = useRef(null);
  const previousScrollHeight = useRef(0);

  // Debug: Log when allUsers changes
  useEffect(() => {
    console.log('👥 FloatingChatWindow received users:', allUsers?.length || 0);
  }, [allUsers]);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Expose method to parent component to open chat with specific user
  useImperativeHandle(ref, () => ({
    openChatWithUser: async (user) => {
      setIsOpen(true);
      setIsMinimized(false); // Make sure it's not minimized when opening

      // Wait for authentication if needed
      if (!isAuthenticated) {
        try {
          await authenticateWithFirebase();
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Failed to authenticate:', error);
          return;
        }
      }

      // Wait a moment for state to update
      await new Promise(resolve => setTimeout(resolve, 100));
      await handleUserSelect(user);
    }
  }));

  useEffect(() => {
    // Only auto-scroll to bottom for new messages, not when loading older ones
    if (!loadingOlderMessages) {
      scrollToBottom();
    }
  }, [allMessages, loadingOlderMessages]);

  // Authenticate with Firebase when chat opens
  useEffect(() => {
    if (isOpen && !isAuthenticated) {
      const authenticate = async () => {
        setAuthLoading(true);
        try {
          await authenticateWithFirebase();
          setIsAuthenticated(true);
          console.log('Firebase authentication successful');
        } catch (error) {
          console.error('Failed to authenticate with Firebase:', error);
          alert('Failed to connect to chat service. Please try again.');
          setIsOpen(false);
        } finally {
          setAuthLoading(false);
        }
      };
      authenticate();
    }
  }, [isOpen, isAuthenticated]);

  // Listen to user's chats
  useEffect(() => {
    if (isOpen && isAuthenticated && currentUser?.employee_id) {
      const userId = currentUser.employee_id?.toString();
      unsubscribeChats.current = getUserChats(userId, (chats) => {
        setUserChats(chats);
      });
    }

    return () => {
      if (unsubscribeChats.current) {
        unsubscribeChats.current();
      }
    };
  }, [isOpen, isAuthenticated, currentUser]);

  // Listen to messages in selected chat
  useEffect(() => {
    if (selectedChat && currentUser?.employee_id) {
      console.log('📩 Opening chat with ID:', selectedChat.id);

      // Reset state for new chat
      setAllMessages([]);
      setHasMoreMessages(true);
      setLoadingOlderMessages(false);

      unsubscribeMessages.current = listenToMessages(selectedChat.id, (msgs) => {
        setMessages(msgs);
        setAllMessages(msgs); // Initialize with real-time messages
      });

      // Reset unread count when chat is opened
      const userId = currentUser.employee_id?.toString();
      resetUnreadCount(selectedChat.id, userId);
    }

    return () => {
      if (unsubscribeMessages.current) {
        unsubscribeMessages.current();
      }
    };
  }, [selectedChat, currentUser]);

  // Handle scroll event for lazy loading
  const handleScroll = async () => {
    const container = messagesContainerRef.current;
    if (!container || loadingOlderMessages || !hasMoreMessages) return;

    // Check if scrolled near the top (within 100px)
    if (container.scrollTop < 100 && allMessages.length > 0) {
      await loadMoreMessages();
    }
  };

  // Load older messages
  const loadMoreMessages = async () => {
    if (loadingOlderMessages || !hasMoreMessages || !selectedChat) return;

    setLoadingOlderMessages(true);

    try {
      // Get the oldest message timestamp
      const oldestMessage = allMessages[0];
      if (!oldestMessage || !oldestMessage.timestamp) {
        setHasMoreMessages(false);
        setLoadingOlderMessages(false);
        return;
      }

      console.log('📜 Loading older messages before:', oldestMessage.timestamp);

      // Store current scroll position
      const container = messagesContainerRef.current;
      previousScrollHeight.current = container?.scrollHeight || 0;

      // Fetch older messages
      const olderMessages = await loadOlderMessages(selectedChat.id, oldestMessage.timestamp, 30);

      if (olderMessages.length === 0) {
        setHasMoreMessages(false);
        console.log('📭 No more older messages');
      } else {
        console.log(`📬 Loaded ${olderMessages.length} older messages`);

        // Merge older messages with existing ones (older messages go to the beginning)
        setAllMessages(prevMessages => {
          // Remove duplicates based on message id
          const existingIds = new Set(prevMessages.map(m => m.id));
          const newOlderMessages = olderMessages.filter(m => !existingIds.has(m.id));
          return [...newOlderMessages, ...prevMessages];
        });

        // Restore scroll position after new messages are added
        setTimeout(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            const scrollDiff = newScrollHeight - previousScrollHeight.current;
            container.scrollTop = scrollDiff;
          }
        }, 50);
      }
    } catch (error) {
      console.error('Error loading older messages:', error);
    } finally {
      setLoadingOlderMessages(false);
    }
  };

  // Update allMessages when new real-time messages arrive
  useEffect(() => {
    if (messages.length > 0) {
      setAllMessages(prevMessages => {
        // Merge real-time messages with older loaded messages
        const existingIds = new Set(prevMessages.map(m => m.id));
        const newMessages = messages.filter(m => !existingIds.has(m.id));

        // If there are no older messages loaded yet, just use the real-time messages
        if (prevMessages.length === 0 || prevMessages.length === messages.length) {
          return messages;
        }

        // Otherwise, keep older messages and append new real-time messages
        const olderMessages = prevMessages.filter(m => !messages.find(msg => msg.id === m.id));
        return [...olderMessages, ...messages];
      });
    }
  }, [messages]);

  const handleUserSelect = async (user) => {
    setLoading(true);
    try {
      console.log('Current user:', currentUser);
      console.log('Selected user:', user);

      // Validate current user has employee_id
      if (!currentUser?.employee_id) {
        throw new Error('Current user employee_id is missing');
      }

      const chatId = await createIndividualChat(
        {
          employee_id: currentUser.employee_id,
          name: `${currentUser.first_name} ${currentUser.last_name}`,
          email: currentUser.email || '',
          userColor: currentUser.userColor || '0xFFFFFFFF'
        },
        {
          employee_id: user.employee_id,
          name: `${user.first_name} ${user.last_name}`,
          email: user.email || '',
          role: user.role || '',
          userColor: user.userColor || '0xFFFFFFFF'
        }
      );

      console.log('Chat created/found with ID:', chatId);

      // Find the chat in userChats if it exists
      let chat = userChats.find(c => c.id === chatId);

      if (!chat) {
        // If chat not in userChats (new chat with no messages), create a temporary chat object
        chat = {
          id: chatId,
          name: `${user.first_name} ${user.last_name}`,
          type: 'individual',
          participants: {
            0: {
              id: currentUser.employee_id,
              name: `${currentUser.first_name} ${currentUser.last_name}`,
              email: currentUser.email || '',
              userColor: currentUser.userColor || '0xFFFFFFFF'
            },
            1: {
              id: user.employee_id,
              name: `${user.first_name} ${user.last_name}`,
              email: user.email || '',
              userColor: user.userColor || '0xFFFFFFFF'
            }
          },
          unreadCount: 0
        };
      }

      setSelectedChat(chat);
      setShowUserList(false);
    } catch (error) {
      console.error('Error selecting user:', error);
      console.error('Error details:', error.message, error.stack);
      alert(`Failed to start chat: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || !selectedChat) return;

    try {
      const currentUserName = `${currentUser.first_name} ${currentUser.last_name}`;

      // Prepare replyTo payload if replying to a message
      const replyToPayload = replyingTo ? {
        id: replyingTo.id,
        content: replyingTo.content,
        contentType: replyingTo.contentType || 'text',
        senderName: replyingTo.senderName
      } : null;

      await sendMessage(
        selectedChat.id,
        currentUser.employee_id,  // ✅ Use employee_id instead of id
        currentUserName,
        messageInput.trim(),
        'text',
        replyToPayload
      );
      setMessageInput('');
      setReplyingTo(null); // Clear reply state after sending
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  const filteredUsers = allUsers.filter(user =>
    user.id !== currentUser?.id &&
    (`${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
     user.employee_id?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Calculate total unread count
  const totalUnread = userChats.reduce((sum, chat) => sum + (chat.unreadCount || 0), 0);

  if (!isOpen) {
    return (
      <div className="floating-chat-button" onClick={() => setIsOpen(true)}>
        <BsChatDots size={24} />
        {totalUnread > 0 && (
          <Badge bg="danger" className="chat-badge">{totalUnread}</Badge>
        )}
      </div>
    );
  }

  if (isMinimized) {
    return (
      <div className="floating-chat-minimized" onClick={() => setIsMinimized(false)}>
        <BsChatDots size={20} />
        <span style={{ marginLeft: '8px', fontWeight: 'bold' }}>
          {selectedChat ? selectedChat.name : 'Messages'}
        </span>
        {totalUnread > 0 && (
          <Badge bg="danger" style={{ marginLeft: '8px' }}>{totalUnread}</Badge>
        )}
      </div>
    );
  }

  return (
    <div className="floating-chat-window">
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>
            {selectedChat ? (
              <BsChevronDown
                size={20}
                style={{ cursor: 'pointer', marginRight: '8px' }}
                onClick={() => {
                  setSelectedChat(null);
                  setShowUserList(true);
                }}
              />
            ) : null}
            {selectedChat ? selectedChat.name : 'Messages'}
          </span>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {/* Minimize button */}
            <span
              style={{ cursor: 'pointer', fontSize: '20px', fontWeight: 'bold' }}
              onClick={() => setIsMinimized(true)}
              title="Minimize"
            >
              −
            </span>
            {/* Close button */}
            <BsX size={24} style={{ cursor: 'pointer' }} onClick={() => {
              setIsOpen(false);
              setIsMinimized(false);
            }} title="Close" />
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          {showUserList ? (
            <div className="chat-user-list">
              <div className="p-2">
                <Form.Control
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="sm"
                />
              </div>

              {/* Recent Chats */}
              {userChats.length > 0 && (
                <>
                  <div className="px-3 py-2 text-muted" style={{ fontSize: '12px' }}>
                    Recent Chats
                  </div>
                  <ListGroup variant="flush">
                    {userChats.map(chat => {
                      const otherUser = Object.values(chat.participants || {}).find(
                        p => p.id !== currentUser.employee_id
                      );
                      return (
                        <ListGroup.Item
                          key={chat.id}
                          action
                          onClick={() => {
                            setSelectedChat(chat);
                            setShowUserList(false);
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <BsPersonCircle size={32} className="flex-shrink-0 chat-icon-green"  />
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                              <div className="d-flex justify-content-between align-items-center">
                                <strong className="text-truncate" style={{ marginRight: '8px' }}>
                                  {otherUser?.name || chat.name}
                                </strong>
                                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                  {chat.unreadCount > 0 && (
                                    <Badge bg="danger" pill>{chat.unreadCount}</Badge>
                                  )}
                                  <small className="text-muted" style={{ whiteSpace: 'nowrap' }}>
                                    {formatTimestamp(chat.lastMessageTime)}
                                  </small>
                                </div>
                              </div>
                              {chat.lastMessage && (
                                <small className="text-muted text-truncate d-block" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {chat.lastMessage}
                                </small>
                              )}
                            </div>
                          </div>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                </>
              )}

              {/* All Users */}
              <div className="px-3 py-2 text-muted" style={{ fontSize: '12px' }}>
                All Users {allUsers.length > 0 && `(${allUsers.length})`}
              </div>
              <ListGroup variant="flush">
                {allUsers.length === 0 ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" variant="secondary" />
                    <div className="text-muted mt-2" style={{ fontSize: '14px' }}>
                      Loading users...
                    </div>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-4">
                    <div className="text-muted" style={{ fontSize: '14px' }}>
                      No users found
                    </div>
                  </div>
                ) : (
                  filteredUsers.map(user => (
                    <ListGroup.Item
                      key={user.id}
                      action
                      onClick={() => handleUserSelect(user)}
                      disabled={loading}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <BsPersonCircle size={32} chat-icon-green />
                        <div>
                          <div>{`${user.first_name} ${user.last_name}`}</div>
                          <small className="text-muted">{user.employee_id}</small>
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))
                )}
              </ListGroup>
            </div>
          ) : (
            <div className="chat-messages">
              <div className="messages-container" ref={messagesContainerRef} onScroll={handleScroll}>
                {/* Loading indicator for older messages */}
                {loadingOlderMessages && (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" variant="primary" />
                    <small className="text-muted ms-2">Loading older messages...</small>
                  </div>
                )}

                {/* No more messages indicator */}
                {!hasMoreMessages && allMessages.length > 50 && (
                  <div className="text-center py-2">
                    <small className="text-muted">No more messages</small>
                  </div>
                )}

                {allMessages.map((msg, index) => {
                  const isUnread = msg.senderId !== currentUser.employee_id && msg.status !== '2';
                  const isSent = msg.senderId === currentUser.employee_id;
                  return (
                    <div
                      key={msg.id || index}
                      className={`message ${isSent ? 'sent' : 'received'} ${isUnread ? 'unread' : ''}`}
                    >
                      <div
                        className="message-bubble"
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setReplyingTo({
                            id: msg.id,
                            content: msg.content,
                            contentType: msg.contentType || 'text',
                            senderName: msg.senderName
                          });
                        }}
                      >
                        {msg.senderId !== currentUser.employee_id && (
                          <div className="message-sender">{msg.senderName}</div>
                        )}

                        {/* Show replied message preview if this is a reply */}
                        {msg.replyTo && (
                          <div className="reply-preview">
                            <div className="reply-sender">{msg.replyTo.senderName}</div>
                            <div className="reply-content">{msg.replyTo.content}</div>
                          </div>
                        )}

                        <div className="message-content">{msg.content}</div>
                        <div className="message-time">
                          {formatTimestamp(msg.timestamp)}
                          {isUnread && <span className="unread-indicator">●</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              <Form onSubmit={handleSendMessage} className="message-input-form">
                {/* Reply preview bar */}
                {replyingTo && (
                  <div className="replying-to-bar">
                    <div className="flex-grow-1">
                      <div className="reply-to-sender">{replyingTo.senderName}</div>
                      <div className="reply-to-content">{replyingTo.content}</div>
                    </div>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => setReplyingTo(null)}
                      className="p-0"
                    >
                      <BsX size={20} />
                    </Button>
                  </div>
                )}

                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    placeholder="Type a message..."
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                  />
                  <Button type="submit" variant="primary" disabled={!messageInput.trim()}>
                    <BsSend />
                  </Button>
                </div>
              </Form>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
});

FloatingChatWindow.displayName = 'FloatingChatWindow';

export default FloatingChatWindow;
